/**
 * Dentes por numeração FDI (ISO 3950) — permanentes (11–48) e decíduos (51–85). O texto salvo nos
 * itens (pedido de prótese, plano de tratamento) é a lista FDI separada por vírgula, ou o rótulo de
 * arcada inteira: o campo aceita 120 caracteres, e os 32 permanentes passariam disso.
 */
export const FDI_UPPER_RIGHT = ["18", "17", "16", "15", "14", "13", "12", "11"];
export const FDI_UPPER_LEFT = ["21", "22", "23", "24", "25", "26", "27", "28"];
export const FDI_LOWER_LEFT = ["31", "32", "33", "34", "35", "36", "37", "38"];
export const FDI_LOWER_RIGHT = ["41", "42", "43", "44", "45", "46", "47", "48"];
export const FDI_UPPER_ARCH = [...FDI_UPPER_RIGHT, ...FDI_UPPER_LEFT];
export const FDI_LOWER_ARCH = [...FDI_LOWER_RIGHT, ...FDI_LOWER_LEFT];

export const DECIDUO_SUPERIOR_DIREITO = ["55", "54", "53", "52", "51"];
export const DECIDUO_SUPERIOR_ESQUERDO = ["61", "62", "63", "64", "65"];
export const DECIDUO_INFERIOR_ESQUERDO = ["71", "72", "73", "74", "75"];
export const DECIDUO_INFERIOR_DIREITO = ["85", "84", "83", "82", "81"];
export const ARCADA_DECIDUA_SUPERIOR = [...DECIDUO_SUPERIOR_DIREITO, ...DECIDUO_SUPERIOR_ESQUERDO];
export const ARCADA_DECIDUA_INFERIOR = [...DECIDUO_INFERIOR_DIREITO, ...DECIDUO_INFERIOR_ESQUERDO];

export type Denticao = "permanente" | "decidua";

const PERMANENTES = new Set([...FDI_UPPER_ARCH, ...FDI_LOWER_ARCH]);
const DECIDUOS = new Set([...ARCADA_DECIDUA_SUPERIOR, ...ARCADA_DECIDUA_INFERIOR]);

export const ehDeciduo = (fdi: string) => DECIDUOS.has(fdi);
export const denticaoDoDente = (fdi: string): Denticao => (DECIDUOS.has(fdi) ? "decidua" : "permanente");

const ROTULOS: Record<string, string[]> = {
  "arcada completa": [...FDI_UPPER_ARCH, ...FDI_LOWER_ARCH],
  "arcada superior": FDI_UPPER_ARCH,
  "arcada inferior": FDI_LOWER_ARCH,
  "arcada decídua completa": [...ARCADA_DECIDUA_SUPERIOR, ...ARCADA_DECIDUA_INFERIOR],
  "arcada decídua superior": ARCADA_DECIDUA_SUPERIOR,
  "arcada decídua inferior": ARCADA_DECIDUA_INFERIOR,
};

/** Texto salvo → lista FDI. Aceita rótulos de arcada (separados por vírgula entre si e com dentes)
 *  e ignora o que não for dente (texto livre vira lista vazia). Sem repetir, na ordem lida. */
export function parseDentes(texto: string | null | undefined): string[] {
  if (!texto) return [];
  const saida: string[] = [];
  for (const parte of texto.split(",").map((p) => p.trim()).filter(Boolean)) {
    const rotulo = ROTULOS[parte.toLowerCase()];
    const dentes = rotulo ?? (PERMANENTES.has(parte) || DECIDUOS.has(parte) ? [parte] : []);
    for (const d of dentes) if (!saida.includes(d)) saida.push(d);
  }
  return saida;
}

/** Lista FDI → texto salvo. Arcada inteira de cada dentição vira o rótulo. */
export function formatarDentes(selecionados: string[]): string | null {
  if (selecionados.length === 0) return null;
  const tem = (lista: string[]) => lista.every((f) => selecionados.includes(f));
  const partes: string[] = [];
  const usados = new Set<string>();
  const arcada = (sup: string[], inf: string[], completa: string, superior: string, inferior: string) => {
    if (tem(sup) && tem(inf)) {
      partes.push(completa);
      [...sup, ...inf].forEach((d) => usados.add(d));
    } else if (tem(sup)) {
      partes.push(superior);
      sup.forEach((d) => usados.add(d));
    } else if (tem(inf)) {
      partes.push(inferior);
      inf.forEach((d) => usados.add(d));
    }
  };
  arcada(FDI_UPPER_ARCH, FDI_LOWER_ARCH, "Arcada completa", "Arcada superior", "Arcada inferior");
  arcada(ARCADA_DECIDUA_SUPERIOR, ARCADA_DECIDUA_INFERIOR, "Arcada decídua completa", "Arcada decídua superior", "Arcada decídua inferior");
  return [...partes, ...selecionados.filter((d) => !usados.has(d))].join(", ");
}

/** Idade em anos completos na data de hoje. */
export function idadeEmAnos(nascimento: Date | string, hoje = new Date()): number {
  const n = new Date(nascimento);
  let idade = hoje.getFullYear() - n.getFullYear();
  if (hoje.getMonth() < n.getMonth() || (hoje.getMonth() === n.getMonth() && hoje.getDate() < n.getDate())) idade--;
  return idade;
}

/** Abre na decídua quando o item já tem dente decíduo ou o paciente tem menos de 6 anos. */
export function denticaoInicial(opcoes: { dentes?: string[]; nascimento?: Date | string | null; hoje?: Date }): Denticao {
  if (opcoes.dentes?.some(ehDeciduo)) return "decidua";
  if (opcoes.dentes?.length) return "permanente";
  if (opcoes.nascimento && idadeEmAnos(opcoes.nascimento, opcoes.hoje) < 6) return "decidua";
  return "permanente";
}

/** A arcada visível só mexe na sua dentição: a parte da outra fica como estava (dentição mista). */
export function trocarParteDaSelecao(atual: string[], denticao: Denticao, novaParte: string[]): string[] {
  return [...atual.filter((d) => denticaoDoDente(d) !== denticao), ...novaParte.filter((d) => denticaoDoDente(d) === denticao)];
}
